// Owns the iOS Epassi return leg: recognizing and classifying the return URL
// (handleEpassiAppUrlOpen, called from AppUrlListener's single appUrlOpen listener)
// and the in-memory channel that hands the result to EPassiMobileReturnHandler.
// The token must never travel through the router URL, where analytics pageview
// tracking would see it — hence this module instead of a redirect to the web return
// route. The result is buffered so a return delivered before the handler has mounted
// (e.g. cold start) is not lost.

import { config } from '../../utils/config';
import { isValidEpassiToken, parseEpassiMobileReturn } from '../../utils/epassi';
import { registerAppUrlHandler } from '../../appUrlHandlers';

export type EpassiMobileResult = { kind: 'token'; token: string } | { kind: 'failed' };

let pendingResult: EpassiMobileResult | null = null;
let subscriber: ((result: EpassiMobileResult) => void) | null = null;

// A single-slot, single-subscriber in-memory channel that carries the mobile
// Epassi return from the URL listener to the React handler without the token
// touching the router. The producer is handleEpassiAppUrlOpen (below), driven by
// AppUrlListener's appUrlOpen listener; the consumer is EPassiMobileReturnHandler,
// which subscribes once it has mounted and the user query has settled. Because the
// return URL can arrive before the handler mounts (cold start), a result with no
// live subscriber is buffered and replayed to the first subscriber that attaches.
export function deliverEpassiMobileResult(result: EpassiMobileResult): void {
  if (subscriber) {
    subscriber(result);
    return;
  }
  // Latest wins — only one authorization round-trip can be meaningful at a time.
  pendingResult = result;
}

export function subscribeEpassiMobileResult(
  consumer: (result: EpassiMobileResult) => void
): () => void {
  subscriber = consumer;
  if (pendingResult) {
    const result = pendingResult;
    pendingResult = null;
    consumer(result);
  }
  return () => {
    if (subscriber === consumer) subscriber = null;
  };
}

// iOS Epassi wallet authorization return: fi.epassi.auth.{partner}://token/epassi?token=...
// The URL carries the encrypted wallet token and must never reach the router
// (analytics reads the location) or any log — hand it off in memory instead.
// Returns true when the URL was an Epassi return and has been consumed.
export function handleEpassiAppUrlOpen(
  url: string,
  cfg: { epassiEnabled: boolean; epassiPartnerName: string } = config
): boolean {
  if (!cfg.epassiEnabled || !cfg.epassiPartnerName) return false;
  const { isEpassiReturn, token } = parseEpassiMobileReturn(url, cfg.epassiPartnerName);
  if (!isEpassiReturn) return false;
  deliverEpassiMobileResult(
    isValidEpassiToken(token) ? { kind: 'token', token } : { kind: 'failed' }
  );
  return true;
}

registerAppUrlHandler(handleEpassiAppUrlOpen);
